/**
 * InterestOpportunitiesModal.jsx
 * DAS-2427: Read-only market overview for the manual-account view.
 *
 * Opens as a sub-modal from AccountPortfolioModal when the user clicks the
 * "Interest Opportunities" button on a manual (non-broker) account view.
 * Shows two tables side-by-side:
 *   1. Savings (immediate access) — top 10 banks by rate from Savings_Rate_Market
 *   2. Deposito (fixed term)      — best bank per 3M / 6M / 9M / 1Y from Spaarinformatie_Data
 *
 * DAS-2571: on the Savings table the user can now (a) click a bank name to open
 * its website in a new tab, (b) ignore a bank (greyed + no link), and (c) see how
 * much they already hold at each bank.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table, Spin, Alert, Tag, Tooltip, Button } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import api from '../functions/api';


const fmtEur = (v) =>
  `€${Math.round(v).toLocaleString('nl-NL')}`;

const InterestOpportunitiesModal = ({ visible, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [togglingBank, setTogglingBank] = useState(null);

  const loadData = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    api.get(`/savings_opportunities/${userId}`)
      .then((resp) => {
        if (resp.data && resp.data.success) {
          setData(resp.data);
        } else {
          setError(resp.data?.error || 'Failed to load opportunities');
        }
      })
      .catch((err) => {
        console.error('DAS-2427: savings_opportunities fetch failed:', err);
        setError(err.response?.data?.error || err.message || 'Network error');
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if (!visible || !userId) return;
    setData(null);
    loadData();
  }, [visible, userId, loadData]);

  // DAS-2571: flip the ignore flag for a bank, then refresh the list.
  const toggleIgnore = async (bankName, nextIgnored) => {
    setTogglingBank(bankName);
    try {
      await api.put(`/savings_bank_preference/${userId}`, {
        bank_name: bankName,
        is_ignored: nextIgnored,
      });
      loadData();
    } catch (err) {
      console.error('DAS-2571: toggle ignore failed:', err);
    } finally {
      setTogglingBank(null);
    }
  };

  const savingsColumns = [
    {
      title: '#',
      key: 'rank',
      width: 40,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'Bank',
      dataIndex: 'bank_name',
      key: 'bank_name',
      render: (text, row, idx) => {
        // DAS-2571: ignored → greyed, struck through, no link.
        if (row.is_ignored) {
          return (
            <span style={{ color: '#bbb', textDecoration: 'line-through' }}>
              {text}
            </span>
          );
        }
        const inner = idx === 0 ? <strong>{text}</strong> : text;
        return row.url ? (
          <a href={row.url} target="_blank" rel="noopener noreferrer">
            {inner}
          </a>
        ) : inner;
      },
    },
    {
      // DAS-2571: how much the user already holds at this bank.
      title: 'You hold',
      dataIndex: 'user_holdings',
      key: 'user_holdings',
      width: 110,
      align: 'right',
      render: (v, row) => {
        if (!v) return <span style={{ color: '#ccc' }}>—</span>;
        return (
          <span style={{ color: row.is_ignored ? '#bbb' : '#555' }}>
            {fmtEur(v)}
          </span>
        );
      },
    },
    {
      title: 'Rate',
      dataIndex: 'rate_pct',
      key: 'rate_pct',
      width: 130,
      align: 'right',
      render: (v, row, idx) => {
        if (v == null) return '—';
        const formatted = `${v.toFixed(2)}%`;
        if (row.is_ignored) return <span style={{ color: '#bbb' }}>{formatted}</span>;
        const rateNode = idx === 0
          ? <Tag color="green" style={{ fontWeight: 'bold' }}>{formatted}</Tag>
          : <span>{formatted}</span>;
        // DAS-2613: the ongoing (regular) rate is shown as the headline; a
        // temporary new-customer actierente is surfaced as a labelled bonus so
        // it is never mistaken for the rate an existing customer earns.
        if (row.promo_rate_pct == null) return rateNode;
        const promoLabel = `actie ${row.promo_rate_pct.toFixed(2)}%`
          + (row.promo_ends ? ` tot ${row.promo_ends}` : '');
        return (
          <span>
            {rateNode}
            <Tooltip title={`New-customer promo rate ${row.promo_rate_pct.toFixed(2)}%`
              + (row.promo_ends ? `, ends ${row.promo_ends}` : '')
              + `. Regular ongoing rate is ${formatted}.`}>
              <Tag color="orange" style={{ marginLeft: 4, marginRight: 0, fontSize: 10, lineHeight: '16px' }}>
                {promoLabel}
              </Tag>
            </Tooltip>
          </span>
        );
      },
    },
    {
      // DAS-2571: ignore / un-ignore toggle.
      title: '',
      key: 'ignore',
      width: 44,
      align: 'center',
      render: (_, row) => (
        <Tooltip title={row.is_ignored ? 'Ignored — click to bring back' : "Ignore (a bank that doesn't interest you)"}>
          <Button
            type="text"
            size="small"
            loading={togglingBank === row.bank_name}
            icon={row.is_ignored ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => toggleIgnore(row.bank_name, !row.is_ignored)}
          />
        </Tooltip>
      ),
    },
  ];

  // Deposito table: one row per canonical term bucket.
  const depositoRows = data
    ? ['3M', '6M', '9M', '1Y'].map((term) => {
        const info = data.deposito_top_by_term?.[term];
        return {
          key: term,
          term,
          bank_name: info?.bank_name ?? null,
          rate_pct: info?.rate_pct ?? null,
          user_holdings: info?.user_holdings ?? null,
        };
      })
    : [];

  const depositoColumns = [
    {
      title: 'Term',
      dataIndex: 'term',
      key: 'term',
      width: 80,
      render: (t) => <strong>{t}</strong>,
    },
    {
      title: 'Best bank',
      dataIndex: 'bank_name',
      key: 'bank_name',
      render: (text) => text ?? <span style={{ color: '#999' }}>—</span>,
    },
    {
      // DAS-2571 follow-up: how much the user already holds at this bank.
      title: 'You hold',
      dataIndex: 'user_holdings',
      key: 'user_holdings',
      width: 110,
      align: 'right',
      render: (v) => {
        if (!v) return <span style={{ color: '#ccc' }}>—</span>;
        return <span style={{ color: '#555' }}>{fmtEur(v)}</span>;
      },
    },
    {
      title: 'Rate',
      dataIndex: 'rate_pct',
      key: 'rate_pct',
      width: 90,
      align: 'right',
      render: (v) => v == null
        ? <span style={{ color: '#999' }}>—</span>
        : <Tag color="green" style={{ fontWeight: 'bold' }}>{`${v.toFixed(2)}%`}</Tag>,
    },
  ];

  return (
    <Modal
      title="Interest Opportunities"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin tip="Loading market rates…" />
        </div>
      )}
      {error && (
        <Alert
          type="error"
          message="Could not load opportunities"
          description={error}
          showIcon
        />
      )}
      {data && !loading && !error && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 420px', minWidth: 420 }}>
            <h3 style={{ marginTop: 0 }}>
              Savings (immediate access){' '}
              <Tag color="blue">spaarrente.nl</Tag>
            </h3>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>
              Top 10 banks by current variable rate. Click a bank to open its
              site; use the eye to ignore one.{' '}
              {data.savings_scraped_at
                ? `Scraped ${data.savings_scraped_at}.`
                : 'Freshness: unknown.'}
            </div>
            <Table
              dataSource={data.savings_top || []}
              columns={savingsColumns}
              rowKey={(r, idx) => `${r.bank_name}-${idx}`}
              pagination={false}
              size="small"
            />
          </div>
          <div style={{ flex: '1 1 360px', minWidth: 360 }}>
            <h3 style={{ marginTop: 0 }}>
              Deposito (fixed term){' '}
              <Tag color="purple">spaarinformatie.nl</Tag>
            </h3>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>
              Best rate per term bucket. Compare with the rate locked on your
              own deposito accounts.
            </div>
            <Table
              dataSource={depositoRows}
              columns={depositoColumns}
              pagination={false}
              size="small"
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InterestOpportunitiesModal;
